import {
  type FetchResult,
  type MutationResult,
  type UnscheduleSendInboxDraftMutation,
  useUnscheduleSendInboxDraftMutation,
} from "@qw/graphql";
import { useToast } from "@qw/ui";
import { useCallback } from "react";

type UseUnscheduleSendDraftReturn = [
  (id: string) => Promise<FetchResult<UnscheduleSendInboxDraftMutation> | undefined>,
  MutationResult<UnscheduleSendInboxDraftMutation>,
];

export const useUnscheduleSendDraft = (): UseUnscheduleSendDraftReturn => {
  const { toast } = useToast();
  const [unscheduleSendDraftMutation, result] = useUnscheduleSendInboxDraftMutation();

  const unscheduleSendDraft = useCallback(
    (id: string) =>
      unscheduleSendDraftMutation({
        variables: { id },
        onCompleted: (data) => {
          if (!data?.unscheduleSendInboxDraft) {
            throw new Error("Failed to unschedule draft for sending");
          }
          toast({
            title: "Draft unscheduled",
            description: "Your draft has been unscheduled for sending.",
          });
        },
        onError: (error) => {
          toast({
            title: "Failed to unschedule draft for sending",
            description: error instanceof Error ? error.message : "An unexpected error occurred",
            variant: "destructive",
          });
        },
        refetchQueries: ["InboxDrafts", "InboxDraft", "InboxDraftsCount", "InboxDraftsFailedScheduledSendsCount"],
      }),
    [unscheduleSendDraftMutation, toast]
  );

  return [unscheduleSendDraft, result];
};
